interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

/**
 * ページ番号の配列を生成する。
 * 7ページ以下: 全ページ表示。
 * 8ページ以上: 常に5つのページ番号 + 先頭/末尾 + 省略記号。
 *
 * 例 (total=10):
 *   current=1  → [1, 2, 3, 4, 5, '...', 10]
 *   current=5  → [1, '...', 3, 4, 5, 6, 7, '...', 10]
 *   current=9  → [1, '...', 6, 7, 8, 9, 10]
 */
function getPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [];

  // 中央ウィンドウの範囲を計算（5ページ分）
  let start = current - 2;
  let end = current + 2;

  // 端に寄っている場合は調整
  if (start < 1) {
    start = 1;
    end = 5;
  }
  if (end > total) {
    end = total;
    start = total - 4;
  }

  // 先頭
  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push('...');
  }

  // 中央ウィンドウ
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // 末尾
  if (end < total) {
    if (end < total - 1) pages.push('...');
    pages.push(total);
  }

  return pages;
}

export function Pagination({ currentPage, totalPages, onPageChange, isLoading }: PaginationProps) {
  if (totalPages <= 1) return null;

  const pages = getPageNumbers(currentPage, totalPages);

  return (
    <nav aria-label="ページネーション" className="flex items-center justify-center gap-1 py-4">
      <button
        aria-label="前へ"
        disabled={currentPage <= 1 || isLoading}
        onClick={() => onPageChange(currentPage - 1)}
        className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        &lt; 前へ
      </button>

      {pages.map((page, i) =>
        page === '...' ? (
          <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-slate-400">
            ...
          </span>
        ) : (
          <button
            key={page}
            aria-label={String(page)}
            aria-current={page === currentPage ? 'page' : undefined}
            disabled={isLoading}
            onClick={() => onPageChange(page)}
            className={`min-w-[2.25rem] rounded-lg px-2 py-1.5 text-sm font-medium ${
              page === currentPage ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            } disabled:cursor-not-allowed disabled:opacity-40`}
          >
            {page}
          </button>
        ),
      )}

      <button
        aria-label="次へ"
        disabled={currentPage >= totalPages || isLoading}
        onClick={() => onPageChange(currentPage + 1)}
        className="rounded-lg px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        次へ &gt;
      </button>
    </nav>
  );
}
